/**
 * Dynamic Variant Picker - FIXED
 * 
 * Root cause found: The metafield JSON was outputting ALL variant metafields
 * (face_shape, finishing, weight, etc.) but NOT the swatch_image key.
 * 
 * This version:
 * 1. Correctly reads swatch_image from the metafield JSON
 * 2. Uses WeakMap to track variant ID per label (no URL comparison)
 * 3. Force-updates images when variant changes
 */

const VARIANT_PICKER_CONFIG = {
  excludeFromUpdates: ['Face Shape']
};

class DynamicVariantPicker {
  constructor() {
    this.variantPickers = document.querySelectorAll('hdt-variant-picker');
    this.productData = {};
    this.variantSwatchImages = {}; // variant ID → swatch image URL
    this.labelState = new WeakMap(); // label element → { variantId }
    this.init();
  }

  init() {
    this.removeOldPreviews();
    this.loadSwatchImages();
    this.addStyles();
    this.variantPickers.forEach(picker => {
      const handle = picker.getAttribute('handle');
      if (handle) this.fetchProductData(handle, picker);
    });
  }

  removeOldPreviews() {
    document.querySelectorAll('#variant-hover-preview, .variant-hover-preview').forEach(el => el.remove());
  }

  loadSwatchImages() {
    // Source 1: Liquid <script> JSON tags
    document.querySelectorAll('script[id^="variant-metafields-"]').forEach(script => {
      try {
        const data = JSON.parse(script.textContent);
        if (data && typeof data === 'object') {
          for (const [vid, meta] of Object.entries(data)) {
            const id = vid.trim();
            // The meta could be:
            // Format A (correct): { "swatch_image": "url" }
            // Format B (old/wrong): { "id": ..., "title": ..., "option1": ..., ... }
            if (typeof meta === 'object' && meta.swatch_image) {
              this.variantSwatchImages[id] = meta.swatch_image;
            }
          }
        }
      } catch (e) {
        console.error('[VP] Parse error:', e);
      }
    });

    // Source 2: DOM input[data-variant-id] attributes
    document.querySelectorAll('input[data-variant-id]').forEach(input => {
      const vid = input.getAttribute('data-variant-id')?.trim();
      if (!vid || this.variantSwatchImages[vid]) return;

      const swatch = input.getAttribute('data-swatch-image');
      if (swatch?.trim()) {
        this.variantSwatchImages[vid] = swatch.trim();
        return;
      }
      const featured = input.getAttribute('data-featured-image');
      if (featured?.trim()) {
        this.variantSwatchImages[vid] = featured.trim();
      }
    });

    const count = Object.keys(this.variantSwatchImages).length;
    console.log(`[VP] Swatch images loaded: ${count} variants`);
    
    // Show sample
    const keys = Object.keys(this.variantSwatchImages).slice(0, 2);
    keys.forEach(k => console.log(`  ${k}: ${this.variantSwatchImages[k]}`));
  }

  async fetchProductData(handle, picker) {
    try {
      const resp = await fetch(`/products/${handle}.js`);
      const product = await resp.json();
      this.productData[handle] = product;

      // Fallback: variant featured_image from product.js
      product.variants.forEach(v => {
        const vid = v.id.toString();
        if (!this.variantSwatchImages[vid] && v.featured_image?.src) {
          this.variantSwatchImages[vid] = v.featured_image.src;
        }
      });

      // Coverage report
      let covered = 0;
      product.variants.forEach(v => {
        if (this.variantSwatchImages[v.id.toString()]) covered++;
      });
      console.log(`[VP] Product: "${product.title}" | ${covered}/${product.variants.length} variants have images`);

      if (covered === 0) {
        console.warn(`[VP] ⚠️ NO swatch images found! Check that variant-metafields-script.liquid outputs swatch_image correctly.`);
        console.log(`[VP] Sample variant from product.js:`, product.variants[0]?.id);
        console.log(`[VP] Metafield keys:`, Object.keys(this.variantSwatchImages).slice(0, 5));
      }

      this.setupPicker(picker, product);
    } catch (e) {
      console.error('[VP] Fetch error:', e);
    }
  }

  setupPicker(picker, product) {
    picker.querySelectorAll('fieldset').forEach((fs, idx) => {
      fs.querySelectorAll('input[type="radio"]').forEach(input => {
        input.addEventListener('change', () => {
          console.log(`\n========== VARIANT CHANGE ==========`);
          console.log(`Changed: "${product.options[idx]?.name}" → "${input.value}"`);
          this.updateAllImages(picker, product);
        });
      });
    });
    console.log(`\n========== INITIAL LOAD ==========`);
    this.updateAllImages(picker, product);
  }

  getCurrentSelections(picker) {
    const sel = [];
    picker.querySelectorAll('fieldset').forEach(fs => {
      const c = fs.querySelector('input[type="radio"]:checked');
      sel.push(c ? c.value : null);
    });
    return sel;
  }

  /**
   * Find the swatch image URL for a given option value + current selections.
   * Returns { url, variantId } or null.
   */
  findSwatchImage(product, selections, optionIndex, optionValue) {
    const testOpts = [...selections];
    testOpts[optionIndex] = optionValue;

    // 1. Exact match
    const exact = product.variants.find(v =>
      testOpts.every((opt, i) => opt === null || v.options[i] === opt)
    );
    if (exact) {
      const url = this.variantSwatchImages[exact.id.toString()];
      if (url) return { url, variantId: exact.id.toString() };
    }

    // 2. Fallback: match current Material + this option value (ignore other options)
    // This ensures we get the RIGHT material's image
    const materialSelection = selections[0]; // Material is always option 0
    if (materialSelection && optionIndex !== 0) {
      const materialMatch = product.variants.find(v =>
        v.options[0] === materialSelection && v.options[optionIndex] === optionValue
      );
      if (materialMatch) {
        const url = this.variantSwatchImages[materialMatch.id.toString()];
        if (url) return { url, variantId: materialMatch.id.toString() };
      }
    }

    // 3. Last resort: any variant with this option value
    const any = product.variants.find(v => {
      if (v.options[optionIndex] !== optionValue) return false;
      return !!this.variantSwatchImages[v.id.toString()];
    });
    if (any) {
      return { url: this.variantSwatchImages[any.id.toString()], variantId: any.id.toString() };
    }

    return null;
  }

  updateAllImages(picker, product) {
    const selections = this.getCurrentSelections(picker);
    const fieldsets = picker.querySelectorAll('fieldset');

    console.log(`Selections: ${selections.map((s, i) => `${product.options[i]?.name}: "${s}"`).join(' | ')}`);

    let stats = { updated: 0, created: 0, sameVariant: 0, noImage: 0 };

    fieldsets.forEach((fieldset, fIdx) => {
      const optName = product.options[fIdx]?.name || `Option ${fIdx + 1}`;

      if (VARIANT_PICKER_CONFIG.excludeFromUpdates.some(ex =>
        optName.toLowerCase().includes(ex.toLowerCase()))) return;

      const labels = fieldset.querySelectorAll('label[for]');

      labels.forEach(label => {
        const inputId = label.getAttribute('for');
        const input = inputId ? document.getElementById(inputId) : null;
        if (!input) return;

        const optValue = input.value;
        const result = this.findSwatchImage(product, selections, fIdx, optValue);

        if (!result) {
          stats.noImage++;
          console.log(`⚠️ ${optName} → "${optValue}" | NO IMAGE`);
          return;
        }

        // Skip only if same variant was already applied
        const prevState = this.labelState.get(label);
        if (prevState && prevState.variantId === result.variantId) {
          stats.sameVariant++;
          return;
        }

        // Update state
        this.labelState.set(label, { variantId: result.variantId });

        const newSrc = this.sizedUrl(result.url, 160);
        const newSrcset = this.srcset(result.url, [50, 100, 160, 200]);

        // Find or create img
        const existingImg = label.querySelector('hdt-tooltip img') || label.querySelector('img');

        if (existingImg) {
          existingImg.setAttribute('src', newSrc);
          existingImg.setAttribute('srcset', newSrcset);
          existingImg.src = newSrc;
          existingImg.srcset = newSrcset;
          stats.updated++;
          console.log(`✅ ${optName} → "${optValue}" | v:${result.variantId} | UPDATED | ${newSrc}`);
        } else {
          const tooltip = label.querySelector('hdt-tooltip');
          const container = tooltip || label;
          const pattern = container.querySelector('.hdt-form-color-pattern');

          const img = document.createElement('img');
          img.src = newSrc;
          img.srcset = newSrcset;
          img.sizes = '50px';
          img.loading = 'lazy';
          img.alt = optValue;
          img.width = 200;
          img.height = 200;

          if (pattern) pattern.replaceWith(img);
          else container.insertBefore(img, container.firstChild);

          label.classList.add('hdt-product-form_value_has-image');
          stats.created++;
          console.log(`✅ ${optName} → "${optValue}" | v:${result.variantId} | CREATED | ${newSrc}`);
        }
      });
    });

    console.log(`\nSummary: ${stats.updated} updated | ${stats.created} new | ${stats.sameVariant} same-variant | ${stats.noImage} no-image`);
    console.log(`=========================================\n`);
  }

  sizedUrl(src, w) {
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    if (src.includes('cdn.shopify.com')) {
      try { const u = new URL(src); u.searchParams.set('width', w); return u.toString(); }
      catch(e) { return src.replace(/width=\d+/, `width=${w}`); }
    }
    return src;
  }

  srcset(src, ws) {
    if (!src) return '';
    return ws.map(w => `${this.sizedUrl(src, w)} ${w}w`).join(', ');
  }

  addStyles() {
    const e = document.getElementById('variant-picker-styles');
    if (e) e.remove();
    const s = document.createElement('style');
    s.id = 'variant-picker-styles';
    s.textContent = `.variant-hover-preview, #variant-hover-preview { display: none !important; }`;
    document.head.appendChild(s);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new DynamicVariantPicker());
} else {
  new DynamicVariantPicker();
}
document.addEventListener('shopify:section:load', () => new DynamicVariantPicker());

// Prevent tooltip from showing on face shapes
document.addEventListener('DOMContentLoaded', function() {
  const faceShapeBox = document.querySelector('.remove_tooltip');
  
  if (faceShapeBox) {
    // Remove hover events
    const labels = faceShapeBox.querySelectorAll('.ymq-options-img-label');
    labels.forEach(label => {
      label.addEventListener('mouseenter', function(e) {
        e.stopPropagation();
        const tooltip = document.querySelector('.just-tooltip');
        if (tooltip) {
          tooltip.style.display = 'none';
        }
      });
      
      label.addEventListener('click', function(e) {
        const tooltip = document.querySelector('.just-tooltip');
        if (tooltip) {
          tooltip.style.display = 'none';
        }
      });
    });
  }
});


document.addEventListener('DOMContentLoaded', function () {
  // YMQ → HDT sync
  document.querySelectorAll('.ymq-attrib-15tem24').forEach(function (ymqInput) {
    ymqInput.addEventListener('change', function () {
      const val = this.value;
      const hdtInput = document.querySelector(
        `fieldset[data-index="2"] input[name="Side Engravings"][value="${val}"]`
      );
      if (hdtInput && !hdtInput.checked) {
        hdtInput.checked = true;
        hdtInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });

  // HDT → YMQ sync
  document.querySelectorAll('fieldset[data-index="2"] input[name="Side Engravings"]').forEach(function (hdtInput) {
    hdtInput.addEventListener('change', function () {
      const val = this.value;
      const ymqInput = document.querySelector(
        `.ymq-attrib-15tem24[value="${val}"]`
      );
      if (ymqInput && !ymqInput.checked) {
        ymqInput.checked = true;
        ymqInput.dispatchEvent(new Event('change', { bubbles: true }));
        // Also update YMQ hidden input
        const hiddenInput = document.querySelector('input.ymq-hidden-input[name="properties[Side Engravings]"]');
        if (hiddenInput) hiddenInput.value = val;
        // Update YMQ display text
        const textSpan = document.querySelector('#option-box-15tem24 .ymq_option_text_span');
        if (textSpan) textSpan.textContent = val;
      }
    });
  });
});